import React, { useState, useCallback, useMemo } from 'react'
import {
  Card,
  Form,
  Input,
  Typography,
  Flex,
  ColorPicker,
  Upload,
  Button,
  Image,
  Select,
  Switch,
} from 'antd'
import {
  UploadOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons'
import type { Color } from 'antd/es/color-picker'
import type { FlowBranding } from '@/data'
import { getImages, type LibraryImage } from '@/api/lop/images/getImages'
import { toBool } from '@/utils/toBool'

const { Text } = Typography

type LogoMode = 'upload' | 'library'

export interface BrandingColorField {
  key: 'primary_color' | 'secondary_color' | 'accent_color' | 'cta_color'
  label: string
}

export interface BrandingCustomColorField {
  key: string
  label: string
  default: string | boolean
  /** 'toggle' renders a switch for boolean flags */
  type?: 'color' | 'toggle'
}

type PaletteField =
  | ({ kind: 'standard' } & BrandingColorField)
  | ({ kind: 'custom' } & BrandingCustomColorField)

export interface BrandingEditorConfig {
  title: string
  colorFields?: BrandingColorField[]
  customColorFields?: BrandingCustomColorField[]
  /** Show the mini logo section (URL + widths stored under brand.custom) */
  miniLogo?: boolean
}

const MINI_LOGO_KEYS = [
  'miniLogoUrl',
  'miniLogoDesktopWidth',
  'miniLogoMobileWidth',
] as const

const defaultConfig: BrandingEditorConfig = { title: 'Cobranding' }

interface BrandingEditorProps {
  branding: FlowBranding
  onChange: (branding: FlowBranding) => void
  enabled: boolean
  onToggle: (enabled: boolean) => void
  config?: BrandingEditorConfig
}

function useLibraryImages() {
  const [images, setImages] = useState<LibraryImage[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  const fetchImages = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getImages()
      setImages(result)
    } catch {
      setImages([])
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }, [])

  if (!fetched && !loading) {
    fetchImages()
  }

  const options = useMemo(
    () =>
      images.map((img) => ({
        value: img.url,
        label: img.name,
      })),
    [images],
  )

  return { images, options, loading }
}

export const BrandingEditor: React.FC<BrandingEditorProps> = ({
  branding,
  onChange,
  enabled,
  onToggle,
  config = defaultConfig,
}) => {
  const [logoMode, setLogoMode] = useState<LogoMode>('upload')
  const {
    images: libraryImages,
    options: libraryOptions,
    loading: libraryLoading,
  } = useLibraryImages()

  const updateField = <K extends keyof FlowBranding>(
    key: K,
    value: FlowBranding[K],
  ) => {
    onChange({ ...branding, [key]: value })
  }

  const handleColorChange =
    (key: BrandingColorField['key']) => (color: Color) =>
      updateField(key, color.toHexString())

  const handleCustomColorChange = (key: string) => (color: Color) => {
    onChange({
      ...branding,
      custom: { ...(branding.custom ?? {}), [key]: color.toHexString() },
    })
  }

  const handleCustomToggleChange = (key: string) => (checked: boolean) => {
    onChange({
      ...branding,
      custom: { ...(branding.custom ?? {}), [key]: checked },
    })
  }

  const handleCustomTextChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...branding,
        custom: { ...(branding.custom ?? {}), [key]: e.target.value },
      })
    }

  // Edit mode: a saved mini logo means the section starts expanded
  const [showMiniLogo, setShowMiniLogo] = useState(() =>
    Boolean(branding.custom?.miniLogoUrl),
  )

  const handleMiniLogoToggle = (checked: boolean) => {
    setShowMiniLogo(checked)
    if (!checked && branding.custom) {
      const custom = { ...branding.custom }
      MINI_LOGO_KEYS.forEach((key) => delete custom[key])
      onChange({ ...branding, custom })
    }
  }

  const hasPalette = Boolean(
    config.colorFields?.length || config.customColorFields?.length,
  )

  const allPaletteFields: PaletteField[] = [
    ...(config.colorFields ?? []).map((f) => ({
      kind: 'standard' as const,
      ...f,
    })),
    ...(config.customColorFields ?? []).map((f) => ({
      kind: 'custom' as const,
      ...f,
    })),
  ]
  const isToggle = (f: PaletteField) =>
    f.kind === 'custom' && f.type === 'toggle'
  // Toggles render first, then all color pickers
  const paletteFields = [
    ...allPaletteFields.filter(isToggle),
    ...allPaletteFields.filter((f) => !isToggle(f)),
  ]

  const renderPaletteField = (field: PaletteField) => {
    if (field.kind === 'custom' && field.type === 'toggle') {
      return (
        <Form.Item
          key={field.key}
          label={field.label}
          style={{ marginBottom: 8, width: 200 }}
        >
          <Switch
            checked={toBool(branding.custom?.[field.key] ?? field.default)}
            onChange={handleCustomToggleChange(field.key)}
          />
        </Form.Item>
      )
    }
    return (
      <Form.Item
        key={field.key}
        label={field.label}
        style={{ marginBottom: 8, width: 200 }}
      >
        {field.kind === 'standard' ? (
          <ColorPicker
            value={branding[field.key]}
            onChange={handleColorChange(field.key)}
            showText
          />
        ) : (
          <ColorPicker
            value={String(branding.custom?.[field.key] ?? field.default)}
            onChange={handleCustomColorChange(field.key)}
            showText
          />
        )}
      </Form.Item>
    )
  }

  const handleRemoveLogo = () => {
    onChange({ ...branding, logo: null, logo_url: null, logo_name: null })
  }

  const handleLibrarySelect = (url: string) => {
    const image = libraryImages.find((img) => img.url === url)
    onChange({
      ...branding,
      logo: null,
      logo_url: url,
      logo_name: image?.name ?? null,
    })
  }

  // New file upload takes priority, otherwise show existing URL from BE
  const logoPreview =
    branding.logo instanceof File
      ? URL.createObjectURL(branding.logo)
      : branding.logo_url || null

  return (
    <Card
      title={
        <Flex align="center" gap="middle">
          <Switch checked={enabled} onChange={onToggle} />
          <span>{config.title}</span>
        </Flex>
      }
      style={{ maxWidth: 1100 }}
    >
      {!enabled ? (
        <Text type="secondary">
          Enable {config.title.toLowerCase()} to customize the logo and color
          palette for this flow.
        </Text>
      ) : (
        <Form layout="vertical">
          <Form.Item label="Partner Logo">
            <Flex vertical gap="small">
              {logoPreview && (
                <Flex align="center" gap="middle">
                  <Image
                    src={logoPreview}
                    alt={branding.logo_alt || 'Partner logo'}
                    height={48}
                    preview={false}
                    style={{ objectFit: 'contain', maxWidth: 200 }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveLogo}
                    size="small"
                  >
                    Remove
                  </Button>
                </Flex>
              )}

              {logoMode === 'upload' ? (
                <Flex gap="small">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      updateField('logo', file)
                      return false
                    }}
                  >
                    <Button icon={<UploadOutlined />}>
                      {logoPreview ? 'Replace Logo' : 'Upload Logo'}
                    </Button>
                  </Upload>
                  <Button
                    icon={<FolderOpenOutlined />}
                    onClick={() => setLogoMode('library')}
                  >
                    Select from Library
                  </Button>
                </Flex>
              ) : (
                <Flex gap="small" style={{ width: '100%' }}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Search library..."
                    style={{ flex: 1 }}
                    loading={libraryLoading}
                    value={
                      !branding.logo && branding.logo_url
                        ? branding.logo_url
                        : undefined
                    }
                    onChange={handleLibrarySelect}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={libraryOptions}
                  />
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => setLogoMode('upload')}
                  >
                    Upload Instead
                  </Button>
                </Flex>
              )}
            </Flex>
          </Form.Item>

          <Form.Item label="Logo Alt Text">
            <Input
              value={branding.logo_alt}
              onChange={(e) => updateField('logo_alt', e.target.value)}
              placeholder="e.g. Partner Company Name"
            />
          </Form.Item>

          {config.miniLogo && (
            <>
              <Form.Item label="Mini Logo">
                <Switch
                  checked={showMiniLogo}
                  onChange={handleMiniLogoToggle}
                />
              </Form.Item>
              {showMiniLogo && (
                <>
                  <Form.Item label="Mini Logo URL">
                    <Flex vertical gap="small">
                      {Boolean(branding.custom?.miniLogoUrl) && (
                        <Image
                          src={String(branding.custom?.miniLogoUrl)}
                          alt="Mini logo"
                          height={32}
                          preview={false}
                          style={{ objectFit: 'contain', maxWidth: 140 }}
                        />
                      )}
                      <Input
                        value={String(branding.custom?.miniLogoUrl ?? '')}
                        onChange={handleCustomTextChange('miniLogoUrl')}
                        placeholder="https://..."
                      />
                    </Flex>
                  </Form.Item>
                  <Flex gap="middle">
                    <Form.Item label="Desktop Width" style={{ width: 200 }}>
                      <Input
                        value={String(
                          branding.custom?.miniLogoDesktopWidth ?? '',
                        )}
                        onChange={handleCustomTextChange(
                          'miniLogoDesktopWidth',
                        )}
                        placeholder="98px"
                      />
                    </Form.Item>
                    <Form.Item label="Mobile Width" style={{ width: 200 }}>
                      <Input
                        value={String(
                          branding.custom?.miniLogoMobileWidth ?? '',
                        )}
                        onChange={handleCustomTextChange('miniLogoMobileWidth')}
                        placeholder="60px"
                      />
                    </Form.Item>
                  </Flex>
                </>
              )}
            </>
          )}

          {hasPalette && (
            <>
              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                Color Palette
              </Text>
              <Flex wrap gap="middle">
                {paletteFields.map(renderPaletteField)}
              </Flex>
            </>
          )}
        </Form>
      )}
    </Card>
  )
}
